import type { ComponentType } from 'react'
import {
  forwardRef,
  lazy,
  Suspense,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import { openNewUserOnboardingDialog } from 'src/features/NewUserOnboarding'
import { testMediaQuery } from 'src/utils/hooks/useBreakpoint'
import { endEventGroup, startEventGroup, track } from 'src/utils/tracker'
import { isAppWebview } from 'src/utils/userAgent'
import { UIDialog } from '../ui/Modal'
import { getLastSuccessfulSignInMethod } from './lastSignInMethod'
import type {
  OpenSignModalEventData,
  SignModalProps,
  SignModalRef,
} from './types'

// Eagerly preload SignNew. Once resolved, `SignNewResolved` holds the
// component synchronously so we can skip the Suspense/lazy overhead.
let SignNewResolved: ComponentType<any> | null = null
let signNewPromise: Promise<typeof import('./SignNew')> | null = null
const importSignNew = () => {
  signNewPromise ??= import('./SignNew').then(m => {
    SignNewResolved = m.default
    return m
  })
  return signNewPromise
}
const SignNewLazy = lazy(importSignNew) // fallback for first render if not yet resolved

/** Preload SignNew chunk. Cached — safe to call multiple times. */
export const preloadSignNew = () => {
  importSignNew()
}

// Eagerly preload after page becomes idle (eliminates delay on mobile
// where there is no hover event to trigger the preload).
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => importSignNew())
} else {
  setTimeout(() => importSignNew(), 3000)
}

/**
 * Replicate SignModal using RAC
 * Should has the same behavior as SignModal
 */
const SignModalRACContent = forwardRef<SignModalRef, SignModalProps>(
  (
    {
      open: openInit = false,
      onSuccess,
      onClose,
      disableClose,
      oauthRedirectUrl,
      skipAfterAuthDialogs,
      isLoginInit = false,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(openInit)
    // Returning users default to the sign-in tab.
    const [isLogin, setIsLogin] = useState(
      () => isLoginInit || !!getLastSuccessfulSignInMethod(),
    )
    const navigate = useNavigate()

    useEffect(() => {
      if (!open) {
        endEventGroup('login_source')
      }
    }, [open])

    useImperativeHandle(
      ref,
      () => ({
        open: (isSignUp = true) => {
          setIsLogin(!isSignUp)
          setOpen(true)
        },
        close: () => {
          setOpen(false)
        },
      }),
      [],
    )

    // Use synchronously resolved component when available (idle preload),
    // otherwise fall back to lazy() + Suspense.
    const SignComponent: ComponentType<any> = SignNewResolved ?? SignNewLazy

    const skeleton = !SignNewResolved ? (
      <div className="flex flex-col w-full animate-pulse">
        <div className="h-[160px] rounded-t-xl bg-purple-400/60" />
        <div className="px-6 py-4 space-y-3">
          <div className="mx-auto h-9 w-48 rounded-lg bg-zinc-200 dark:bg-zinc-700" />
          <div className="h-10 rounded-xl bg-zinc-200 dark:bg-zinc-700" />
          <div className="h-12 rounded-xl bg-zinc-200 dark:bg-zinc-700" />
          <div className="grid grid-cols-3 gap-2">
            <div className="h-11 rounded-xl bg-zinc-200 dark:bg-zinc-700" />
            <div className="h-11 rounded-xl bg-zinc-200 dark:bg-zinc-700" />
            <div className="h-11 rounded-xl bg-zinc-200 dark:bg-zinc-700" />
          </div>
        </div>
      </div>
    ) : undefined

    return (
      <Suspense fallback={skeleton}>
        <SignComponent
          isLogin={isLogin}
          setIsLogin={setIsLogin}
          onSuccess={() => {
            onSuccess?.(isLogin)
            if (!skipAfterAuthDialogs) {
              if (!isLogin) {
                // Open register onboarding dialog on sign up completes
                openNewUserOnboardingDialog({ navigate })
              }
            }
            setOpen(false)
            onClose?.()
          }}
          onClose={
            disableClose
              ? undefined
              : () => {
                  setOpen(false)
                  onClose?.()
                }
          }
          oauthRedirectUrl={oauthRedirectUrl}
        />
      </Suspense>
    )
  },
)

export function openSignModalRAC(
  props?: SignModalProps | null,
  eventData?: OpenSignModalEventData,
): void {
  startEventGroup('login_source')
  track('signup_login_show', eventData)

  importSignNew() // Kick off chunk download in parallel with dialog mount
  const shouldUseDrawer = isAppWebview() || testMediaQuery('(width < 640px)')

  UIDialog.create({
    dismissable: !props?.disableClose,
    onClose: props?.onClose,
    children: ({ close }) => (
      <SignModalRACContent
        {...props}
        onClose={() => {
          close()
          props?.onClose?.()
        }}
      />
    ),
    type: shouldUseDrawer ? 'drawer' : 'dialog',
    className: !shouldUseDrawer
      ? 'w-[440px] h-[min(680px,85vh)] overflow-y-auto'
      : 'max-w-[480px] mx-auto overflow-y-auto pb-[env(safe-area-inset-bottom)]',
    // On mobile drawer: the layer is resized to the visual viewport so the
    // bottom sheet sits above the virtual keyboard.  The modal itself keeps a
    // stable height — input fields are at the top, bottom whitespace is
    // naturally hidden behind the keyboard.
    ...(shouldUseDrawer && {
      modalClassName: 'h-[max(560px,75dvh)] mt-auto',
      modalLayerProps: {
        className:
          '!top-[var(--visual-viewport-offset-top,0px)] !bottom-auto !h-[var(--visual-viewport-height,100dvh)]',
      },
    }),
  })
}
